import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { formatDateTime } from '@/lib/format';
import type { AuditEventRecord } from '@/lib/firestoreAudit';

type Props = {
  event: AuditEventRecord | null;
  onClose: () => void;
};

/**
 * Modal com o detalhe de um evento de auditoria: ator, alvo, data, motivo e o
 * `before`/`after` em JSON.
 *
 * Origem: sem issue — commit 05030f0d1, 2026-04-26 17:26 +0200 (aba Audit do
 * tenant + página /audit global, Sprint 2).
 *
 * Contrato: `event` nulo = fechado (não renderiza). `before`/`after` são
 * `unknown` gravados pela Function e mostrados com `JSON.stringify` cru — o
 * que estiver lá (inclusive dado de cliente) aparece.
 *
 * Usado por (admin): `AuditList`.
 *
 * Histórico:
 *  - 2026-10-10 15:12 +0200 — (#936) cabeçalho padrão (#796).
 */
export function AuditDiffDialog({ event, onClose }: Props): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  if (!event) return null;

  return (
    <Modal visible onDismiss={onClose} header={event.action} size="large">
      <SpaceBetween size="m">
        <ColumnLayout columns={2} variant="text-grid">
          <Box>
            <Box variant="awsui-key-label">{t('audit.dialog.actor')}</Box>
            <span style={{ fontFamily: 'monospace' }}>{event.actorEmail}</span>
          </Box>
          <Box>
            <Box variant="awsui-key-label">{t('audit.dialog.target')}</Box>
            <span style={{ fontFamily: 'monospace' }}>
              {event.targetType} / {event.targetId}
            </span>
          </Box>
          <Box>
            <Box variant="awsui-key-label">{t('audit.dialog.at')}</Box>
            <span style={{ fontFamily: 'monospace' }}>
              {event.atMillis != null ? formatDateTime(event.atMillis, i18n.language) : '—'}
            </span>
          </Box>
          {event.reason && (
            <Box>
              <Box variant="awsui-key-label">{t('audit.dialog.reason')}</Box>
              {event.reason}
            </Box>
          )}
        </ColumnLayout>

        <ColumnLayout columns={2}>
          <DiffPane label={t('audit.dialog.before')} value={event.before} />
          <DiffPane label={t('audit.dialog.after')} value={event.after} />
        </ColumnLayout>
      </SpaceBetween>
    </Modal>
  );
}

/** Um lado do diff: JSON indentado, ou `—` quando nulo. */
function DiffPane({ label, value }: { label: string; value: unknown }): React.JSX.Element {
  return (
    <SpaceBetween size="xxs">
      <Box variant="awsui-key-label">{label}</Box>
      <pre
        style={{
          overflowX: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontFamily: 'monospace',
          fontSize: 12,
          margin: 0,
        }}
      >
        {value === null || value === undefined ? '—' : JSON.stringify(value, null, 2)}
      </pre>
    </SpaceBetween>
  );
}
